// src/hooks/useSwipeAction.ts
// Lightweight swipe gesture hook for mobile cards.
// No external dependencies — uses vanilla touch events.
import { useRef, useEffect, useCallback } from 'react';

interface SwipeActionOptions {
  onSwipeRight?: () => void;
  onSwipeLeft?: () => void;
  threshold?: number;   // min px to trigger (default 80)
  maxVertical?: number; // max vertical movement allowed (default 30)
}

interface SwipeState {
  startX: number;
  startY: number;
  deltaX: number;
  swiping: boolean;
}

export function useSwipeAction<T extends HTMLElement>(
  options: SwipeActionOptions
) {
  const ref = useRef<T>(null);
  const state = useRef<SwipeState>({ startX: 0, startY: 0, deltaX: 0, swiping: false });
  const { threshold = 80, maxVertical = 30, onSwipeRight, onSwipeLeft } = options;

  const handleTouchStart = useCallback((e: TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    state.current = { startX: touch.clientX, startY: touch.clientY, deltaX: 0, swiping: false };
  }, []);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    const s = state.current;
    const deltaX = touch.clientX - s.startX;
    const deltaY = Math.abs(touch.clientY - s.startY);

    // If vertical scroll exceeds threshold, cancel swipe
    if (deltaY > maxVertical) {
      s.swiping = false;
      if (ref.current) ref.current.style.transform = '';
      return;
    }

    s.deltaX = deltaX;
    s.swiping = true;

    // Visual feedback: translate the card
    if (ref.current) {
      const clamped = Math.max(-120, Math.min(120, deltaX));
      ref.current.style.transform = `translateX(${clamped}px)`;
      ref.current.style.transition = 'none';
    }
  }, [maxVertical]);

  const handleTouchEnd = useCallback(() => {
    const s = state.current;

    // Reset visual
    if (ref.current) {
      ref.current.style.transform = '';
      ref.current.style.transition = 'transform 200ms ease-out';
    }

    if (!s.swiping) {
      state.current = { startX: 0, startY: 0, deltaX: 0, swiping: false };
      return;
    }

    try {
      if (s.deltaX > threshold && onSwipeRight) {
        onSwipeRight();
      } else if (s.deltaX < -threshold && onSwipeLeft) {
        onSwipeLeft();
      }
    } catch (err) {
      console.error('[useSwipeAction] Swipe callback failed:', err);
    } finally {
      state.current = { startX: 0, startY: 0, deltaX: 0, swiping: false };
    }
  }, [threshold, onSwipeRight, onSwipeLeft]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: true });
    el.addEventListener('touchend', handleTouchEnd);

    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  return ref;
}
